import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { DateIndication } from '@/components/DateIndication';

export const RequestedAppUpdateInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  /**
   * === Set Value Watchers section ===
   */
  // const buildNumberValue = watch && watch('buildNumber' as Path<TSchema>);

  const forceAndroidUpdateValue =
    watch && watch('forceAndroidUpdate' as Path<TSchema>);

  const forceIOSUpdateValue = watch && watch('forceIOSUpdate' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  // const dataHaveChanged =
  //   String(buildNumberValue ?? '') !== String(data?.buildNumber ?? '') ||
  //   Boolean(forceAndroidUpdateValue) !== Boolean(data?.forceAndroidUpdate) ||
  //   Boolean(forceIOSUpdateValue) !== Boolean(data?.forceIOSUpdate);

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('buildNumber' as Path<TSchema>, data.buildNumber ?? '');
      setValue(
        'forceAndroidUpdate' as Path<TSchema>,
        data.forceAndroidUpdate ?? false,
      );
      setValue('forceIOSUpdate' as Path<TSchema>, data.forceIOSUpdate ?? false);
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  return (
    <form onSubmit={onSubmit}>
      <Card
        cardClassName="[&_.card-header]:py-1.5 below-sm:[&_.card-header]:py-5"
        title={
          <TitleSection
            defaultIconClassName="mt-2 ki-pad max-sm:-mt-[1px]"
            textClassName="flex flex-wrap flex-1 font-normal"
            text={
              <div className="flex flex-wrap flex-1 items-center gap-5">
                <div className="flex flex-1 font-semibold">
                  {t('common:request')}
                </div>
                {data && <DateIndication t={t} date={data?.updatedAt} />}
              </div>
            }
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:confirm')
            }
            // disabled={submitDisabled || !dataHaveChanged}
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormField
            text={t('appBuildNumber')}
            required
            textWrapperClassName="mt-3 max-md:mt-0"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('buildNumber' as Path<TSchema>))}
                errorMessage={errors && (errors.buildNumber?.message as string)}
                placeholder={t('appBuildNumber')}
                onInput={(e) => {
                  // Enforce to input only a positive number
                  e.currentTarget.value =
                    Number(e.currentTarget.value) > 0
                      ? e.currentTarget.value
                      : null;
                }}
                type="number"
              />
            </div>
          </FormField>
          <FormField
            text={t('forceUpdateOnAndroid')}
            textWrapperClassName="mt-1"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-1">
              <Switch
                {...(register &&
                  register('forceAndroidUpdate' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.forceAndroidUpdate?.message as string)
                }
                checked={forceAndroidUpdateValue ?? false}
              />
            </div>
          </FormField>
          <FormField text={t('forceUpdateOnIOS')} textWrapperClassName="mt-0">
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-0">
              <Switch
                {...(register && register('forceIOSUpdate' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.forceIOSUpdate?.message as string)
                }
                checked={forceIOSUpdateValue ?? false}
              />
            </div>
          </FormField>
        </>
      </Card>
    </form>
  );
};
